import { lazy } from "react";
import { IRC, IRC_Exportable, IRC_Exportable_PPTX_Chart, IRC_Exportable_PPTX_Table } from "../../../types";
import { legend } from "@/core/utils/legend-state/core";
import { Observable } from "@legendapp/state";
import { createContextProvider } from "@/core/utils/context-api";
import { state, IState as IInstance } from "./instance/state";
import { report_builder } from "@/features/report-builder/state";
import pptxgen from "pptxgenjs";
import { sanitizeForCSV } from "@/features/report-builder/rcs/helpers";

const RC_ID = "employee_voice_single_company_correlation";

export const rc: IRC<IInstance.ILocalState$, IInstance.IRemoteState$, { instance_id: string }> = {
  rc_id: RC_ID,
  instance: {
    component: lazy(() =>
      import("./instance/ui/index").then((module) => ({
        default: module.RC_Component,
      })),
    ),
  },
  registry: {
    // state$: Stores local and remote state for all instances
    state$: legend.state.observable<{
      local_instances: Record<string, Observable<IInstance.ILocalState$>>;
      remote_instances: Record<string, Observable<IInstance.IRemoteState$>>;
    }>({
      local_instances: {},
      remote_instances: {},
    }),
    // context: Provides instance context to components
    context: createContextProvider<{
      instance_id: string;
    }>(),
    // actions: Methods for creating/deleting/managing instances
    actions: {
      createInstance: (instance_id, options) => {
        const { local, remote } = state.createState(instance_id, { local: options.local });

        legend.state.batch(() => {
          rc.registry.state$.local_instances[instance_id].set(local);
          rc.registry.state$.remote_instances[instance_id].set(remote);

          report_builder.local.active_rc_registries.assign({
            [RC_ID]: RC_ID,
          });
        });
      },
      deleteInstance: (id) => {
        legend.state.batch(() => {
          rc.registry.state$.remote_instances[id].delete();
          rc.registry.state$.local_instances[id].delete();
          if (rc.registry.actions.isEmpty()) {
            report_builder.local.active_rc_registries[RC_ID].delete();
          }
        });
      },
      cleanUp: () => {
        legend.state.batch(() => {
          rc.registry.state$.remote_instances.set({});
          rc.registry.state$.local_instances.set({});
          report_builder.local.active_rc_registries[RC_ID].delete();
        });
        // TODO: clean up local storage data and remote data if cache is effected
      },
      getInstance: (id) => {
        const remote = rc.registry.state$.remote_instances[id];
        const local = rc.registry.state$.local_instances[id];
        if (!remote) {
          throw new Error(`Remote instance with id ${id} not found`);
        }
        if (!local) {
          throw new Error(`Local instance with id ${id} not found`);
        }
        return { remote, local };
      },
      isEmpty: () => {
        return (
          Object.keys(rc.registry.state$.remote_instances.get()).length === 0 &&
          Object.keys(rc.registry.state$.local_instances.get()).length === 0
        );
      },
      exportInstance: async (id) => {
        const instance = rc.registry.actions.getInstance(id);

        const exportable: IRC_Exportable = {
          folder_path: "How-is-a-company-s-culture-performing",
          master: {
            pptx: {
              powerpoint_name: "How-is-a-company-s-culture-performing",
              merge_views_to_master_pptx: true,
            },
          },
          formats: [
            {
              pptx: {
                powerpoint_name: "How-is-a-company-s-culture-performing",
                slide_data: [
                  {
                    title: "Analysis: Scatter - How is a company's culture performing?",
                    chart: await helpers.views.analysis.visualisation.pptx.scatter(instance),
                  },
                  {
                    title: "Analysis: Table - How is a company's culture performing?",
                    table: await helpers.views.analysis.visualisation.pptx.table(instance),
                  },
                  // {
                  //   title: "Evidence - How is a company's culture performing?",
                  //   table: await helpers.views.analysis.evidence.pptx(instance),
                  // },
                ],
              },
              csv: {
                data: [
                  {
                    name: "Analysis-Data",
                    content: await helpers.views.analysis.visualisation.csv(instance),
                  },
                  // {
                  //   name: "Evidence-Data",
                  //   content: await helpers.views.analysis.evidence.csv(instance),
                  // },
                ],
              },
            },
          ],
        };

        return exportable;
      },
    },
  },
};
// #endregion

const helpers = {
  views: {
    analysis: {
      visualisation: {
        pptx: {
          scatter: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            // measures_data is a remote observable, so we need to await it whilst it is fetched from the server
            const measures_data = await legend.utils.awaitObservable(
              instance.remote.measures.data,
              instance.remote.measures.pending,
              instance.remote.measures.error,
            );

            const chart_data = measures_data.chart_data;

            const peerset = report_builder.remote.peerset.data.get();
            const current_x_measure_id = instance.local.views.analysis.selectors.x_axis_measure.selectedMeasureId.get();
            const current_x_measure = instance.local.config.measure.measures
              .get()
              .find((measure) => measure.id === current_x_measure_id);

            const current_y_measure_id = instance.local.views.analysis.selectors.y_axis_measure.selectedMeasureId.get();
            const current_y_measure = instance.local.config.measure.measures
              .get()
              .find((measure) => measure.id === current_y_measure_id);

            const current_company_id = instance.local.views.analysis.selectors.company.selectedCompanyId.get();
            const current_company = peerset[current_company_id ?? ""];

            // Transform data for each series with hex color conversion
            const x_series_data = {
              name: current_x_measure?.label ?? "",
              values: chart_data.map((item) => item.x),
              dashType: "solid",
            };

            const y_series_data = {
              name: current_y_measure?.label ?? "",
              values: chart_data.map((item) => item.y),
              labels: chart_data.map((item) => item.topic_name),
              dashType: "solid",
            };

            // create PPTX
            // TODO: Find way to change data label font size, make it smaller
            const pptx_slide: IRC_Exportable_PPTX_Chart = {
              type: "scatter",
              data: [x_series_data, y_series_data],
              options: {
                title: `${current_company?.company_name}`,
                showTitle: true,
                titleFontSize: 12,

                x: 0.5,
                y: 0.9,
                w: "90%",
                h: "80%",

                // chartColors: seriesData.map((series) => series.color),
                chartColors: ["#CB009E"],
                showLegend: false,
                legendPos: "b",
                chartArea: { border: { type: "solid", color: "888888", pt: 1 } },
                lineSize: 0, // Set to 0 to remove connecting lines
                showValue: false,

                valAxisMinVal: current_y_measure?.domain?.({ PREV: undefined })[0] ?? undefined,
                valAxisMaxVal: current_y_measure?.domain?.({ PREV: undefined })[1] ?? undefined,
                valAxisTitle: current_y_measure?.label || "",
                showValAxisTitle: true,
                valAxisLabelFontSize: 8,
                valAxisLabelFontFace: "Arial",
                valAxisLabelColor: "363636",

                catAxisTitle: current_x_measure?.label || "",
                catAxisMinVal: current_x_measure?.domain?.({ PREV: undefined })[0] ?? undefined,
                catAxisMaxVal: current_x_measure?.domain?.({ PREV: undefined })[1] ?? undefined,
                catAxisLabelFontSize: 8,
                showCatAxisTitle: true,

                showLabel: true, // Must be set to true or labels will not be shown
                dataLabelPosition: "r",
                dataLabelFontSize: 8,
              },
            };

            return pptx_slide;
          },
          table: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            const measures_data = await legend.utils.awaitObservable(
              instance.remote.measures.data,
              instance.remote.measures.pending,
              instance.remote.measures.error,
            );

            const table_data = measures_data.table_data;

            const appliedFilters = instance.local.views.analysis.filters.applied_filters.topic_name.get() ?? [];
            const groups = legend.features.filter
              .getFiltersByGroup({
                state: instance.local.views.analysis.filters.topic,
              })
              .all.all.filter((filter) => appliedFilters.includes(filter.id));

            const peerset = report_builder.remote.peerset.data.get();

            const selected_company_id = instance.local.views.analysis.selectors.company.selectedCompanyId.get();
            const selected_company = peerset[selected_company_id ?? ""];

            const table: pptxgen.TableRow[] = [];
            const row: pptxgen.TableRow = [
              {
                text: "Measure",
              },
            ];

            groups.forEach((group) => {
              row.push({
                text: group.label,
              });
            });

            table.push(row);

            for (const data of table_data) {
              const row: pptxgen.TableRow = [];

              row.push({ text: data?.measure?.label?.toString() ?? "Unknown Measure" });

              groups.forEach((group) => {
                const measure = data?.[group.id];
                let text = measure.score?.toString();
                if (measure.signal === "NO_SIGNAL") {
                  text = "No Signal";
                }
                if (measure.signal === "LOW_SIGNAL") {
                  text = `Low (${measure.score})`;
                }
                if (measure === undefined) {
                  text = "Unknown Topic";
                }
                row.push({ text });
              });

              table.push(row);
            }

            const output: IRC_Exportable_PPTX_Table = {
              title: `Analysis: Table - ${selected_company?.company_name}`,
              data: table,
              options: {
                x: 0.5,
                y: 0.9,
                w: 9.0,
                h: 5.0,
                color: "363636",
                fill: { color: "F2F2F2" },
                border: { type: "solid" },
                autoPage: true,
                autoPageRepeatHeader: true,
                autoPageHeaderRows: 1,
                fontSize: 8,
                colW: 1.5,
                rowH: 0.3,
                margin: 0.1,
                valign: "middle",
                align: "left",
                autoPageLineWeight: 0.1,
              },
            };

            return output;
          },
        },
        csv: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
          // measures_data is a remote observable, so we need to await it whilst it is fetched from the server
          const measures_data = await legend.utils.awaitObservable(
            instance.remote.measures.data,
            instance.remote.measures.pending,
            instance.remote.measures.error,
          );

          const chart_data = measures_data.chart_data;

          const chart_config = instance.local.views.analysis.chart_config.get();
          const peerset = report_builder.remote.peerset.data.get();

          const current_x_measure_id = instance.local.views.analysis.selectors.x_axis_measure.selectedMeasureId.get();
          const current_x_measure = instance.local.config.measure.measures
            .get()
            .find((measure) => measure.id === current_x_measure_id);

          const current_y_measure_id = instance.local.views.analysis.selectors.y_axis_measure.selectedMeasureId.get();
          const current_y_measure = instance.local.config.measure.measures
            .get()
            .find((measure) => measure.id === current_y_measure_id);

          const current_company_id = instance.local.views.analysis.selectors.company.selectedCompanyId.get();
          const current_company = peerset[current_company_id ?? ""];

          // Create CSV header with measure and topic columns
          const headers = [
            "Company",
            "Topic",
            "X Measure Name",
            "Y Measure Name",
            "X Measure Value",
            "Y Measure Value",
          ];
          const csvContent = [headers.join(",")];

          // Create CSV rows with measure and topic values
          chart_data.forEach((item) => {
            const rowData = [
              current_company?.company_name ?? current_company_id,
              item.topic_name,
              current_x_measure?.label ?? "",
              current_y_measure?.label ?? "",
              item.x,
              item.y,
            ];
            csvContent.push(rowData.join(","));
          });

          const csv = csvContent.join("\n");
          return csv;
        },
      },
      evidence: {
        pptx: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
          // evidence is a remote observable, so we need to await it whilst it is fetched from the server
          const evidenceData = await legend.utils.awaitObservable(
            instance.remote.evidence.data,
            instance.remote.evidence.pending,
            instance.remote.evidence.error,
          );

          // Peerset is not a remote observable, so we can just grab it from the local state
          const peerset = report_builder.remote.peerset.data.get();

          const table: pptxgen.TableRow[] = [];

          table.push([
            { text: "Company" },
            { text: "Topic" },
            { text: "Sentiment" },
            { text: "Capture Date" },
            { text: "Text" },
          ]);

          Object.values(evidenceData).forEach((company_extracts) => {
            company_extracts.forEach((extract) => {
              const company = peerset[extract.company_id];
              table.push([
                { text: company?.company_name },
                { text: extract.topic_name },
                { text: extract.sentiment_type },
                { text: extract.content_date },
                { text: extract.text },
              ]);
            });
          });

          const output: IRC_Exportable_PPTX_Table = {
            title: `Evidence - How is a company's culture performing?`,
            data: table,
            options: {
              x: 0.5,
              y: 0.9,
              w: 9.0,
              color: "363636",
              border: { type: "solid" },
              autoPage: true,
            },
          };

          return output;
        },
        csv: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
          // evidence is a remote observable, so we need to await it whilst it is fetched from the server
          const evidenceData = await legend.utils.awaitObservable(
            instance.remote.evidence.data,
            instance.remote.evidence.pending,
            instance.remote.evidence.error,
          );

          // Peerset is not a remote observable, so we can just grab it from the local state
          const peerset = report_builder.remote.peerset.data.get();

          // Create CSV headers
          const headers = ["Company", "Topic", "Sentiment", "Capture Date", "Text"];
          const csvContent = [headers.join(",")];

          Object.values(evidenceData).forEach((company_extracts) => {
            company_extracts.forEach((extract) => {
              const company = peerset[extract.company_id];
              const rowData = [
                sanitizeForCSV(company?.company_name),
                sanitizeForCSV(extract.topic_name),
                sanitizeForCSV(extract.sentiment_type),
                sanitizeForCSV(extract.content_date),
                sanitizeForCSV(extract.text),
              ];
              csvContent.push(rowData.join(","));
            });
          });

          const csv = csvContent.join("\n");
          return csv;
        },
      },
    },
  },
};
